/**
 * The heads of one written note as the sounds they make.
 *
 * `schedule-part.ts` reads a part bar by bar and finds out what is written on each note; this
 * is where a note reached that way becomes sound. Everything a head is not on its own is
 * settled here: a head that ends a tie lengthens the sound that began it instead of making
 * one, an ornament is struck as the notes nobody wrote, and a spread chord is sounded head
 * by head.
 *
 * Which sound a tie lengthens is the rule in `document/ties.ts`, kept for the whole part and
 * not for one line of it, because the head that ends a tie is found by its pitch and by when
 * it begins and may stand in any voice of any staff.
 */

import { drumOf } from "../document/percussion";
import { midiOf, toSounding } from "../document/pitch";
import type { NotePitch } from "../document/pitch";
import { tieKey } from "../document/ties";
import type { TieLedger, TiedHead } from "../document/ties";
import type { Instrument, NoteEvent, Part } from "../document/types";
import { spreadOf, strokesFor } from "./realize";
import type { Shape } from "./realize";
import type { BarTime, PlayedNote } from "./played";

/** One note of a bar, with what the reading of the bar found out about it. */
export interface Reached {
  readonly event: NoteEvent;
  /** The line of its bar it was read from, and the staff it is written on. */
  readonly line: string;
  readonly staff: number;
  /** Ticks from the start of the bar to where it sounds, and how long it sounds for. */
  readonly start: number;
  readonly ticks: number;
  /** Ticks from the start of the piece as played to where the written note ends. */
  readonly ends: number;
  readonly shape: Shape;
  /** Whether a slur carries on from it. */
  readonly carries: boolean;
  /** Whether it is plucked: a bowed string's note under a `pizz.`. */
  readonly plucked: boolean;
  /** Whether it is played with a mute on, and whether it is bowed at the bridge. */
  readonly muted: boolean;
  readonly bridge: boolean;
}

/** What a part has sounded so far; the first three are index for index with each other. */
export interface Sounded {
  readonly notes: PlayedNote[];
  /** The line each note was read from. */
  readonly lines: string[];
  /** Where each note's chord is written, which is where an *sf* on it stands. */
  readonly struck: number[];
  /** The notes a slur carries on from. */
  readonly carried: Set<number>;
  /** The ties still open, each by the note that began its sound. */
  readonly ties: TieLedger<number>;
}

/** The bar a note is read in, and what the part is playing through it. */
export interface Reading {
  readonly part: Part;
  readonly bar: BarTime;
  readonly playing: Instrument;
  /** The signature the part prints here, which is the scale its ornaments move in. */
  readonly fifths: number;
  readonly marks: boolean;
}

/** Sound every head of a note into what the part has sounded. */
export function soundHeads(sounded: Sounded, reached: Reached, reading: Reading): void {
  const { notes, lines, struck, carried, ties } = sounded;
  const { event, line, start, ticks, shape, carries } = reached;
  const { part, bar, playing, fifths, marks } = reading;
  const at = bar.start + start * bar.rate;
  const length = ticks * bar.rate;
  const atTicks = bar.ticks + start;
  const delays = marks ? spreadOf(event, ticks) : [];

  for (let index = 0; index < event.pitches.length; index += 1) {
    const pitch = event.pitches[index] as NotePitch;
    const drum = drumOf(part.instrument.percussion, pitch.drum);
    const sounding = toSounding(pitch, playing.transposition);
    const midi = drum === null ? midiOf(sounding) : drum.midi;
    const head: TiedHead = {
      key: tieKey(pitch),
      at: atTicks,
      ends: reached.ends,
      staff: reached.staff,
      line,
    };
    const joined = pitch.tieStop ? ties.claim(head) : undefined;

    if (joined !== undefined) {
      const previous = notes[joined];
      if (previous !== undefined) {
        // The mark is written on the note that ends the tie, so it shortens that last
        // stretch of the sound rather than the whole of it.
        notes[joined] = {
          ...previous,
          duration: at + length * shape.length - previous.start,
          durationTicks: atTicks + Math.round(ticks * shape.length) - previous.startTicks,
        };
        // A tied sound is joined to what follows it or not as its last note is.
        if (carries) carried.add(joined);
        else carried.delete(joined);
      }
      if (pitch.tieStart) ties.open(head, joined);
      continue;
    }
    ties.strike(head.key, atTicks);

    const written: PlayedNote = {
      partId: part.id,
      eventId: event.id,
      midi,
      percussion: drum !== null,
      program: playing.midiProgram,
      plucked: reached.plucked,
      muted: reached.muted,
      bridge: reached.bridge,
      start: at,
      duration: length,
      startTicks: atTicks,
      durationTicks: ticks,
      velocity: 0,
      swell: [],
    };

    // A note that is tied is sounded as the tie says and not as a mark says: an
    // ornament over a tied pair belongs to the whole sound, and expanding it here
    // would leave the tie nothing to join to.
    const strokes =
      marks && !pitch.tieStart && !pitch.tieStop ? strokesFor(event, pitch, fifths, ticks) : null;

    if (strokes !== null) {
      for (const stroke of strokes) {
        const held = Math.max(1, Math.round(stroke.ticks * shape.length));
        notes.push({
          ...written,
          midi: midi + stroke.semitones,
          start: bar.start + (start + stroke.start) * bar.rate,
          duration: held * bar.rate,
          startTicks: atTicks + stroke.start,
          durationTicks: held,
        });
        lines.push(line);
        // An ornament is stressed where it begins, not on every note it is played as.
        struck.push(atTicks + stroke.start);
      }
      continue;
    }

    // A spread chord is struck note by note; an ornamented one is not spread, because
    // a chord that both rolls and trills is a sign nobody writes.
    const delay = delays[index] ?? 0;
    const held =
      shape.length === 1 && delay === 0
        ? ticks
        : Math.max(1, Math.round((ticks - delay) * shape.length));
    notes.push({
      ...written,
      start: at + delay * bar.rate,
      duration: held * bar.rate,
      startTicks: atTicks + delay,
      durationTicks: held,
    });
    lines.push(line);
    struck.push(atTicks);
    if (carries) carried.add(notes.length - 1);
    if (pitch.tieStart) ties.open(head, notes.length - 1);
  }
}
